import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { env } from "../env/env";
import { fs } from "../filesystem";
import * as p from "./paths.internal";

// User home directory path
const $baseDirPath = atom(async () => {
	return await p.getBaseDir();
});

// Moment directory path
const $momentDirPath = atom(async () => {
	return await p.getMomentDirPath();
});

// Documents directory path
const $documentsDirPath = atom(async (get): Promise<string> => {
	if (!env.isFilesystemAvailable) {
		throw new Error("FILE_SYSTEM_ERROR");
	}

	const momentDirPath = await get($momentDirPath);
	const fsApi = fs();
	return get(fsApi.pathJoin([momentDirPath, "documents"]));
});

// Environments directory path
export const $environmentsDirPath = atom(async (get): Promise<string> => {
	if (!env.isFilesystemAvailable) {
		throw new Error("FILE_SYSTEM_ERROR");
	}

	const momentDirPath = await get($momentDirPath);
	const fsApi = fs();
	return get(fsApi.pathJoin([momentDirPath, "environments"]));
});

// Document-scoped environments directory path: environments/documents/{documentId}/
export const $documentEnvironmentsDirPath = atomFamily((documentId: string) =>
	atom(async (get): Promise<string> => {
		if (!env.isFilesystemAvailable) {
			throw new Error("FILE_SYSTEM_ERROR");
		}

		const environmentsDirPath = await get($environmentsDirPath);
		const fsApi = fs();
		return get(fsApi.pathJoin([environmentsDirPath, "documents", documentId]));
	})
);

// Document directory path for specific document
const $documentDirPath = atomFamily((documentId: string) =>
	atom(async (get): Promise<string> => {
		const documentsDirPath = await get($documentsDirPath);
		const fsApi = fs();
		return get(fsApi.pathJoin([documentsDirPath, documentId]));
	})
);

const $documentMetadataFilePath = atomFamily((documentId: string) =>
	atom(async (get): Promise<string> => {
		if (!env.isFilesystemAvailable) {
			throw new Error("FILE_SYSTEM_ERROR");
		}

		const documentDirPath = await get($documentDirPath(documentId));
		const fsApi = fs();
		return get(fsApi.pathJoin([documentDirPath, "moment.yml"]));
	})
);

// Moment database path
const $momentDbPath = atom(async (get): Promise<string> => {
	if (!env.isFilesystemAvailable) {
		throw new Error("NO_FILESYSTEM");
	}

	const momentDirPath = await get($momentDirPath);
	const fsApi = fs();
	return get(fsApi.pathJoin([momentDirPath, "moment.db"]));
});

// Page directory path
const $pageDirPath = atomFamily(
	({ documentId, pageId, pagePath }: { documentId: string; pageId: string; pagePath?: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const documentDirPath = await get($documentDirPath(documentId));
			const fsApi = fs();

			if (pagePath) {
				const pageFilePath = await get(fsApi.pathJoin([documentDirPath, pagePath]));
				return get(fsApi.dirname(pageFilePath));
			}

			return get(fsApi.pathJoin([documentDirPath, "pages", pageId]));
		}),
	isEqual
);

// Page file path
const $pageFilePath = atomFamily(
	({ documentId, pageId, pagePath }: { documentId: string; pageId: string; pagePath?: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const documentDirPath = await get($documentDirPath(documentId));
			const fsApi = fs();

			// If an explicit path is provided, use it directly
			if (pagePath) {
				return get(fsApi.pathJoin([documentDirPath, pagePath]));
			}

			// Otherwise, fall back to the default pages/{pageId}/index.md structure
			const pageDirPath = await get($pageDirPath({ documentId, pageId }));
			return get(fsApi.pathJoin([pageDirPath, "index.md"]));
		}),
	isEqual
);

const $pageCanvasFilePath = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const pageDirPath = await get($pageDirPath({ documentId, pageId }));
			const fsApi = fs();
			return get(fsApi.pathJoin([pageDirPath, "index.json"]));
		}),
	isEqual
);

const $pageRelatedFilesDirPath = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const pageDirPath = await get($pageDirPath({ documentId, pageId }));
			const fsApi = fs();
			return get(fsApi.pathJoin([pageDirPath, "related"]));
		}),
	isEqual
);

const $pageRelatedFilePath = atomFamily(
	({ documentId, pageId, filename }: { documentId: string; pageId: string; filename: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const pageRelatedFilesDirPath = await get(
				$pageRelatedFilesDirPath({ documentId, pageId })
			);
			const fsApi = fs();
			return get(fsApi.pathJoin([pageRelatedFilesDirPath, filename]));
		}),
	isEqual
);

// Document-relative file path (for images with src attribute relative to document root)
const $documentRelativeFilePath = atomFamily(
	({ documentId, relativePath }: { documentId: string; relativePath: string }) =>
		atom(async (get): Promise<string> => {
			if (!env.isFilesystemAvailable) {
				throw new Error("NO_FILESYSTEM");
			}

			const documentDirPath = await get($documentDirPath(documentId));
			const fsApi = fs();
			return get(fsApi.pathJoin([documentDirPath, relativePath]));
		}),
	isEqual
);

// No-op implementations for non-desktop environments
const noOpErrorAtom = (error: string) =>
	atom(async (): Promise<string> => {
		throw new Error(error);
	});

// Stable singleton atoms for common no-op error cases.
// Using noOpErrorAtom() inline (e.g., in noOpPathsAPI) creates a new atom on
// every call, which causes infinite re-render loops when consumed via
// `useAtomValue(loadable(...))`.
const noOpNoFilesystemAtom = noOpErrorAtom("NO_FILESYSTEM");
const noOpFileSystemErrorAtom = noOpErrorAtom("FILE_SYSTEM_ERROR");

const noOpStringAtom = atom(async (): Promise<string> => {
	return "";
});

const tauriPathsAPI = {
	getBaseDirPath: () => $baseDirPath,
	getMomentDirPath: () => $momentDirPath,
	getDocumentsDirPath: () => $documentsDirPath,
	getEnvironmentsDirPath: () => $environmentsDirPath,
	getDocumentEnvironmentsDirPath: (documentId: string) =>
		$documentEnvironmentsDirPath(documentId),
	getDocumentDirPath: (documentId: string) => $documentDirPath(documentId),
	getDocumentMetadataFilePath: (documentId: string) => $documentMetadataFilePath(documentId),
	getMomentDbPath: () => $momentDbPath,
	getPageDirPath: (params: { documentId: string; pageId: string; pagePath?: string }) =>
		$pageDirPath(params),
	getPageFilePath: (params: { documentId: string; pageId: string; pagePath?: string }) =>
		$pageFilePath(params),
	getPageCanvasFilePath: (params: { documentId: string; pageId: string }) =>
		$pageCanvasFilePath(params),
	getPageRelatedFilesDirPath: (params: { documentId: string; pageId: string }) =>
		$pageRelatedFilesDirPath(params),
	getPageRelatedFilePath: (params: { documentId: string; pageId: string; filename: string }) =>
		$pageRelatedFilePath(params),
	getDocumentRelativeFilePath: (params: { documentId: string; relativePath: string }) =>
		$documentRelativeFilePath(params),
};

const noOpPathsAPI: typeof tauriPathsAPI = {
	getBaseDirPath: () => noOpStringAtom,
	getMomentDirPath: () => noOpStringAtom,
	getDocumentsDirPath: () => noOpFileSystemErrorAtom,
	getEnvironmentsDirPath: () => noOpFileSystemErrorAtom,
	getDocumentEnvironmentsDirPath: () => noOpFileSystemErrorAtom,
	getDocumentDirPath: () => noOpNoFilesystemAtom,
	getDocumentMetadataFilePath: () => noOpFileSystemErrorAtom,
	getMomentDbPath: () => noOpStringAtom,
	getPageDirPath: () => noOpNoFilesystemAtom,
	getPageFilePath: () => noOpNoFilesystemAtom,
	getPageCanvasFilePath: () => noOpNoFilesystemAtom,
	getPageRelatedFilesDirPath: () => noOpNoFilesystemAtom,
	getPageRelatedFilePath: () => noOpNoFilesystemAtom,
	getDocumentRelativeFilePath: () => noOpNoFilesystemAtom,
};

export const paths = () => {
	const api: typeof tauriPathsAPI = env.isFilesystemAvailable ? tauriPathsAPI : noOpPathsAPI;

	return api;
};
